/**
 * Enrichment Opportunity Service
 *
 * Frontend service for enrichment opportunities API.
 * Handles viewing, filtering, and triggering research for missing product attributes.
 *
 * Part of Story 345: Enrichment Opportunities Dashboard
 *
 * Date: 2026-01-06
 */

import { authFetch } from '@/utils/auth-fetch';

const BASE_URL = '/api/enrichment-opportunities';

// Types
export type OpportunityStatus = 'pending' | 'researching' | 'completed' | 'ignored';
export type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type Recommendation = 'recommended' | 'optional' | 'not_recommended';
export type PriceTier = 'premium' | 'mid' | 'entry';
export type ResearchStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'na';

export interface EnrichmentOpportunity {
  id: string;
  pipelineRunId: string;
  pipelineRunDate: string;
  category: string;
  attributeName: string;
  severity: Severity;
  productCount: number;
  premiumCount: number;
  midTierCount: number;
  entryCount: number;
  estimatedCost: number;
  recommendation: Recommendation;
  status: OpportunityStatus;
  researchStartedAt?: string;
  researchCompletedAt?: string;
  productsResearched?: number;
  valuesFound?: number;
  notAvailableCount?: number;
  failedCount?: number;
  actualCost?: number;
  createdAt: string;
}

export interface OpportunityProduct {
  productId: string;
  priceTier: PriceTier;
  researchStatus: ResearchStatus;
  valueFound?: string;
  researchedAt?: string;
  productTitle: string;
  price?: number;
  productUrl?: string;
}

export interface OpportunityDetails extends EnrichmentOpportunity {
  siteId: string;
  products: OpportunityProduct[];
}

export interface OpportunitySummary {
  pendingCount: number;
  researchingCount: number;
  recommendedCount: number;
  totalProducts: number;
  totalEstimatedCost: number;
}

export interface OpportunityFilters {
  status?: OpportunityStatus;
  category?: string;
  priceTier?: PriceTier | 'all';
  sortBy?: 'recommendation' | 'impact' | 'cost' | 'products';
  limit?: number;
  offset?: number;
}

export interface OpportunityListResponse {
  opportunities: EnrichmentOpportunity[];
  total: number;
  limit: number;
  offset: number;
}

export interface ResearchJob {
  opportunityId: string;
  productCount: number;
  estimatedCost: number;
  estimatedTimeMinutes: number;
}

export interface ResearchProgress {
  processed: number;
  total: number;
  valuesFound: number;
  notAvailable: number;
  failed: number;
  elapsedMs: number;
  estimatedRemainingMs: number;
  successRate: number;
  completed?: boolean;
  error?: string;
}

export interface ResearchResult {
  productsResearched: number;
  valuesFound: number;
  notAvailable: number;
  failed: number;
  actualCost: number;
}

// Summary & Categories
export async function getSummary(siteId: string): Promise<OpportunitySummary> {
  const response = await authFetch(`${BASE_URL}/summary?site_id=${siteId}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch summary');
  }
  return response.json();
}

export async function getCategories(siteId: string): Promise<string[]> {
  const response = await authFetch(`${BASE_URL}/categories?site_id=${siteId}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch categories');
  }
  const data = await response.json();
  return data.categories;
}

// Opportunity CRUD
export async function getOpportunities(
  siteId: string,
  filters: OpportunityFilters = {}
): Promise<OpportunityListResponse> {
  const params = new URLSearchParams({ site_id: siteId });

  if (filters.status) params.append('status', filters.status);
  if (filters.category) params.append('category', filters.category);
  if (filters.priceTier && filters.priceTier !== 'all') params.append('price_tier', filters.priceTier);
  if (filters.sortBy) params.append('sort_by', filters.sortBy);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const response = await authFetch(`${BASE_URL}?${params.toString()}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch opportunities');
  }
  return response.json();
}

export async function getOpportunityDetails(opportunityId: string): Promise<OpportunityDetails> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch opportunity details');
  }
  return response.json();
}

export async function ignoreOpportunity(opportunityId: string): Promise<void> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}/ignore`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to ignore opportunity');
  }
}

// Research Operations
export async function startResearch(opportunityId: string): Promise<ResearchJob> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}/research/start`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to start research');
  }
  const data = await response.json();
  return data.job;
}

export async function executeResearch(opportunityId: string): Promise<void> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}/research/execute`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to execute research');
  }
}

export function subscribeToResearchProgress(
  opportunityId: string,
  onProgress: (progress: ResearchProgress) => void,
  onError: (error: Error) => void
): () => void {
  // Get auth token for SSE
  const token = localStorage.getItem('token');
  const url = `${BASE_URL}/${opportunityId}/research/progress`;

  const eventSource = new EventSource(`${url}?token=${token}`);

  eventSource.onmessage = (event) => {
    try {
      const progress = JSON.parse(event.data);
      onProgress(progress);

      if (progress.completed) {
        eventSource.close();
      }
    } catch (e) {
      console.error('Failed to parse progress event:', e);
    }
  };

  eventSource.onerror = () => {
    onError(new Error('Research progress connection lost'));
    eventSource.close();
  };

  // Return cleanup function
  return () => {
    eventSource.close();
  };
}

export async function triggerRegeneration(
  opportunityId: string
): Promise<{ productCount: number; queued: boolean }> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}/regenerate`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to trigger regeneration');
  }
  return response.json();
}

// N/A Management
export async function markUnavailable(
  opportunityId: string,
  productIds: string[]
): Promise<{ markedCount: number }> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}/mark-unavailable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_ids: productIds }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to mark as unavailable');
  }
  return response.json();
}

export async function markAllUnavailable(
  opportunityId: string
): Promise<{ markedCount: number }> {
  const response = await authFetch(`${BASE_URL}/${opportunityId}/mark-all-unavailable`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to mark all as unavailable');
  }
  return response.json();
}

export async function clearUnavailable(
  productId: string,
  attributeName: string
): Promise<{ cleared: boolean }> {
  const response = await authFetch(`${BASE_URL}/clear-unavailable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_id: productId, attribute_name: attributeName }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to clear unavailable status');
  }
  return response.json();
}

// Manual Trigger
export async function identifyOpportunities(
  pipelineRunId: string
): Promise<{ opportunitiesCreated: number; totalProductsAffected: number; totalEstimatedCost: number }> {
  const response = await authFetch(`${BASE_URL}/identify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline_run_id: pipelineRunId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to identify opportunities');
  }
  return response.json();
}

// Helper functions
export function formatEstimatedTime(minutes: number): string {
  if (minutes < 1) return '< 1 minute';
  if (minutes === 1) return '1 minute';
  if (minutes < 60) return `~${Math.round(minutes)} minutes`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (remainingMinutes === 0) return `~${hours} hour${hours > 1 ? 's' : ''}`;
  return `~${hours}h ${remainingMinutes}m`;
}

export function formatCost(cost: number): string {
  return `$${cost.toFixed(2)}`;
}

export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'outline';
    default:
      return 'secondary';
  }
}

export function getRecommendationLabel(recommendation: Recommendation): string {
  switch (recommendation) {
    case 'recommended':
      return 'Recommended';
    case 'optional':
      return 'Optional';
    case 'not_recommended':
      return 'Not Recommended';
    default:
      return recommendation;
  }
}

export function getPriceTierLabel(tier: PriceTier): string {
  switch (tier) {
    case 'premium':
      return 'Premium (>€500)';
    case 'mid':
      return 'Mid-Tier (€150-500)';
    case 'entry':
      return 'Entry (<€150)';
    default:
      return tier;
  }
}
